/**
 * Item icons — bespoke art for items, SVG fallback for the rest.
 *
 * The ids in `REAL_ICON_IDS` have PNG art under `public/assets/items/<id>.png`
 * (flat-shaded low-poly, primal/stone-age, gold-accented set, on a solid BLACK
 * square background — no alpha; the cell's object-fit:cover + border-radius clip
 * it to a rounded/round tile). Those resolve to the PNG path. This now covers
 * every *reachable* item in the catalog (raw
 * materials + forage/creature drops + the full craftable/Tavern/drop gear and
 * cooked-food catalog, 188 ids). The only remaining fallbacks are dormant
 * tier-3/elite data rows with no v1 source — they get a 1x1 slot/material-color
 * SVG so the HUD never 404s and can still convey "weapon vs armor vs consumable".
 *
 * Material items (IronOre, Leather, BearPelt, ...) that ever lack a PNG get
 * bespoke colors via `MATERIAL_COLOR` so raw materials stay distinguishable.
 *
 * To add more real art: drop `<id>.png` into `public/assets/items/` and add the
 * id to `REAL_ICON_IDS` (the set is the full listing of that directory).
 */
import { ITEMS, type ItemSlot } from "@tribez/shared";

/** Item ids that have a real PNG icon under `public/assets/items/<id>.png`.
 *  This is the full listing of that directory — keep them in sync. */
const REAL_ICON_IDS: ReadonlySet<string> = new Set([
  "AbysmalClaw", "AlbinoCavefish", "AncientArtifactFragment", "Antlers",
  "ArathiCheddar", "ArcPulverizer", "BarbedVanguard", "BarrelPlateArmor",
  "BearPelt", "BearShamansShroud", "BeastmastersVambrace", "Beer",
  "BeerBraisedPheasant", "Berry", "BerserkersBattleAxe", "BlindRainfish",
  "BloodthirstyPirateHat", "BoneshardBlade", "BoneshardBladeMain",
  "BrokenBurningSavage", "BrokenViciousJavelin",
  "BurgundianSmokedBearCutlet", "BurningSavage", "ButchersKnife",
  "CelestialDragon", "CelestialMoon", "CelestialOrb", "CleansingDraught",
  "Codex", "ConjurersCharm", "CravensFishSticker", "CrowKeepersBracers",
  "CursedDruidScepter", "CursedLichScepter", "DaemonEdge",
  "DemonicSoulPrison", "Demonward", "DestructionCatalyst",
  "DiseasedCocktail", "DivineGavel", "DraughtofDecay", "DreadplateArmor",
  "DrunkenBooze", "ElderWand", "ElixirofMastery", "EnigmaticStaff",
  "FaithfulWhirlwind", "FelFireTorch", "FireAmmonite", "Fish", "FishingRod",
  "ForestTrout", "ForestersMace", "ForgottenShredder", "FrostBarrier",
  "FrostNeedle", "GarishGladiatorsGarb", "GiantSunfish", "GlimmerCheese",
  "GoldenHauberk", "GoldenPolearm", "GravesingersClaw",
  "GrilledWolfEntrecote", "HandfulofBerries", "HeavyIronBreastplate",
  "HeavyTowerShield", "Herb", "HighlandGuppy", "HollowBulwark",
  "HuntingMachete", "HuntsmansJacket", "InfusedLumber", "IronBandedBuckler",
  "IronOre", "IronShovel", "IronStuddedVest", "IronbarkArmor", "JewelDanio",
  "JudgmentGavel", "KoboldFireworks", "LambentSunflower", "Lantern",
  "Leather", "LeatherCoat", "LightLeatherShield", "LiquidatorSpellblade",
  "Lumber", "LunarSeersNirvana", "LurkersMantle", "MagnificentChefsHat",
  "MarksmensExcellence", "MasterChefsRollingPin", "MasterRangersMaille",
  "MiningDrill", "MongrelProtector", "MurlocDoomhammer", "MurlocTreat",
  "MysteriousSpoon", "MysticsTribalShield", "NecromancersNecklace",
  "Nightcrawler", "NoxiousSpiderDagger", "NoxiousSpiderDaggerMain",
  "ObedienceRod", "OracleSlayer", "OrbofDarkness", "OutlandishHeartpiercer",
  "PhantomCape", "PhantomEdge", "PoachersBow", "PowderedFlask", "PrimeWand",
  "RadiantGem", "RagingBolter", "RawBearMeat", "RawFox", "RawLamb",
  "RawPheasant", "RawStagMeat", "RawWolfMeat", "ReapersHand",
  "ReapersScythe", "ReforgedCandle", "RepeatingCrossbow", "ReverbingKris",
  "RivenClaymore", "RoastedLamb", "RoastedPheasantWithBerrySauce",
  "RoughBlunderbuss", "RunicChisel", "Sabre", "Sageberry", "Sawtooth",
  "ScatterShotgun", "ScrollPadsoftheDarkMagi", "SeitanCandle", "Shadowshard",
  "Shadowstone", "SharpIronPickaxe", "SinfulTyphoon", "SkeletalFinger",
  "SkirmishersHarness", "SlaveDrill", "SleepingBag", "SlipperyEel",
  "SmokedLambSirloin", "SnipersParapet", "SpectralClawLeft",
  "SpectralClawRight", "SpellBook", "SpicePouch", "SpicedLambFeast",
  "SpicyHerbs", "SpikedShadowstoneScutum", "StagHuntersStiletto", "StagStew",
  "Stone", "StormwizardsRobes", "StripedLurker", "SturdyWoodenStick",
  "TalonsofthePilferer", "TheMurlocket", "TheSickle", "TigerGourami",
  "Torch", "ToxicFrog", "TrebuchetPacked", "TrollNoseBone",
  "TwinThrowingAxes", "UnholyRiftDagger", "UnstableConcoction",
  "VengefulMurlocSkull", "VenomousArbalest", "ViciousJavelin",
  "ViciouslySharpArmblade", "ViciouslySharpArmbladeOff", "VigilantesCrest",
  "WarlocksVestments", "WarmWoolTunic", "WaterScorpion", "WhiteBearFang",
  "WispMothersWrappings", "WitchersEye", "WolfSteak", "Wolfsbane",
  "WoodenStaff", "Wool", "ZephyrsGuard",
]);

const SLOT_COLOR: Record<ItemSlot, string> = {
  "main-hand": "#d24545",
  "offhand": "#e08a3a",
  "armor": "#4a78c8",
  "consumable": "#5aa85a",
  "utility": "#9460c8",
};

/** Per-itemId override — applied before slot-color fallback so RESOURCE_POLICY
 *  material items don't all render identically. Keys are PascalCase ItemIds
 *  matching `RESOURCE_POLICY[*].itemId`. */
const MATERIAL_COLOR: Record<string, string> = {
  IronOre:                 "#7a8a99",
  Leather:                 "#8b5a3c",
  BearPelt:                "#6b3e1f",
  Wool:                    "#dcd6c0",
  InfusedLumber:           "#a87432",
  Sageberry:               "#9b6cce",
  SpicyHerbs:              "#c44d3a",
  Nightcrawler:            "#6b3a82",
  Fish:                    "#5b9bd5",
  AncientArtifactFragment: "#cca64f",
  Berry:                   "#7ec33b",
  Herb:                    "#5dbf6f",
};

const NEUTRAL_COLOR = "#7a8290";

function svgDataUrl(color: string): string {
  // 1x1 solid square — browsers stretch this to fill the cell. Tiny payload,
  // no fetch round-trip.
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'><rect width='1' height='1' fill='${color}'/></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/** True when the item has bespoke PNG art (vs. a generated SVG fallback).
 *  Surfaces that have their own richer placeholder (e.g. the craft menu's
 *  per-slot glyphs) use this to decide whether to swap in the real icon. */
export function hasRealItemIcon(itemId: string): boolean {
  return REAL_ICON_IDS.has(itemId);
}

/** Return an icon URL for an item id. Ids with bespoke art resolve to their
 *  PNG; everything else falls back to a slot/material-colored SVG square so the
 *  HUD never 404s. */
export function itemIconUrl(itemId: string): string {
  if (REAL_ICON_IDS.has(itemId)) return `/assets/items/${itemId}.png`;
  const matColor = MATERIAL_COLOR[itemId];
  if (matColor) return svgDataUrl(matColor);
  const def = ITEMS[itemId];
  const color = def ? SLOT_COLOR[def.slot] ?? NEUTRAL_COLOR : NEUTRAL_COLOR;
  return svgDataUrl(color);
}
